import type { CommunicationChannel, CommunicationDirection } from "@/api/communications";

/**
 * The per-channel rules for logging a communication — ONE definition, read by
 * both forms that let a person choose a channel.
 *
 * 🔴 WHY THIS FILE EXISTS. The buyer "Log contact" modal and the full "Log
 * communication" modal ask the same questions of the same five channels, and
 * each kept its own copy of the answers. They drifted, and the drift was not
 * cosmetic: the quick modal offered all five channels while asking for none
 * of the channel-specific fields the server requires, so Email and Meeting
 * could never be logged from it and Call could not be logged without picking
 * an outcome — three of five channels, one of them the default, each failing
 * as a generic 500.
 *
 * The duplication was known. `LogContactModal.tsx` carried the comment
 * "Mirrors `LogCommunicationModal`'s `CALL_OUTCOMES`, which is not exported"
 * directly above its own copy — an accurate description of a hazard, which is
 * not the same as a guard against it. The precedent for the fix is in the
 * same module: `COMMUNICATION_OWNER_TYPES` in `api/communications.ts` is one
 * list guarded by a test that reads the C# source, written after a placement
 * list drifted and corrupted production rows.
 *
 * ONE SPEC, TWO RENDERING RULES. The spec below says, per channel, whether
 * each field is hidden, optional or required. What a form DOES with that is
 * the form's own rule:
 *
 *   - the full form shows every field that is not hidden — it is the complete
 *     record-keeping surface;
 *   - the quick modal shows what the channel REQUIRES, plus Outcome wherever
 *     the spec offers one, because a call outcome is the single most useful
 *     thing to capture in a hurry. It deliberately does not carry the full
 *     form's optional extras.
 *
 * Both rules are keyed on the DATA — the channel — never on which form is
 * mounted. That is the style guide's rule for this very modal: ask "what does
 * this channel need", not "am I inside the quick form", so a new mount gets
 * the right questions for free.
 *
 * The shape of this file is pinned against `tests/fixtures/communication-channel-rules.json`,
 * which the C# parity test reads too.
 */

/** Whether a form asks for a field at all, and whether it insists. */
export type FieldRule = "hidden" | "optional" | "required";

export interface ChannelRule {
  direction: FieldRule;
  subject: FieldRule;
  outcome: FieldRule;
  modality: FieldRule;
}

/** The channels, in the order both forms list them. */
export const COMMUNICATION_CHANNELS: CommunicationChannel[] = [
  "Call",
  "Voicemail",
  "Email",
  "Sms",
  "Meeting",
  "WebForm",
];

/** Inbound first, as the full form has always listed them. */
export const COMMUNICATION_DIRECTIONS: CommunicationDirection[] = ["Inbound", "Outbound"];

/**
 * What a call or voicemail ended in. A bounded list, not free text — the
 * `Outcome` column is `nvarchar(40)`, and an unbounded box can put anything in
 * it.
 *
 * ⚠️ These four are what the FORMS write; the column is already wider. The
 * 20270301/20270401 backfills mapped the legacy `CallLogs.Disposition` into it
 * and wrote `CallbackRequested`, `WrongNumber`, `Disconnected` and — note —
 * `NoAnswer` with NO SPACE, where we write `No Answer`. So `OutcomeBadge`
 * renders some historical rows untoned, and it does so because of that
 * backfill, not because of the free-text box this list replaces. Untoned is
 * legible, nothing reconciles the two spellings, and neither is in scope here.
 */
export const CALL_OUTCOMES = ["Connected", "No Answer", "Voicemail", "Busy"] as const;

export const MEETING_MODALITIES = ["In-Person", "Video", "PhoneConference"] as const;

/**
 * Preselected, so a meeting is loggable without a second thought. The full
 * form has always done this, which is why its "Modality is required" check is
 * effectively unreachable there.
 */
export const DEFAULT_MEETING_MODALITY: (typeof MEETING_MODALITIES)[number] = "In-Person";

/**
 * How each channel is named on screen. `Sms` reads "SMS" everywhere — the
 * chips, the filters and the inbox already said so; only the quick modal said
 * "Text message", so a specialist logged one thing and found it filed under
 * another.
 */
export const CHANNEL_LABELS: Record<CommunicationChannel, string> = {
  Call: "Call",
  Voicemail: "Voicemail",
  Email: "Email",
  Sms: "SMS",
  Meeting: "Meeting",
  WebForm: "Web form",
};

/**
 * 🔴 `required` is the only value the SERVER enforces
 * (`CommunicationLogService.ValidateChannelFields`): Subject for Email,
 * Outcome for Call, Modality for Meeting. `optional` and `hidden` are
 * questions a form asks or declines to ask — the server takes either.
 *
 * Direction is the exception: it is a client-side rule only. The column is
 * nullable and the server has no arm for it; a meeting is simply not
 * directional, so both forms hide it and send null.
 */
export const CHANNEL_RULES: Record<CommunicationChannel, ChannelRule> = {
  Call: { direction: "required", subject: "hidden", outcome: "required", modality: "hidden" },
  Voicemail: { direction: "required", subject: "hidden", outcome: "optional", modality: "hidden" },
  Email: { direction: "required", subject: "required", outcome: "hidden", modality: "hidden" },
  Sms: { direction: "required", subject: "hidden", outcome: "hidden", modality: "hidden" },
  Meeting: { direction: "hidden", subject: "optional", outcome: "hidden", modality: "required" },
  // A web contact form submission (D32): directional like a message, with an
  // optional subject line (the form's own subject field), and nothing else.
  WebForm: { direction: "required", subject: "optional", outcome: "hidden", modality: "hidden" },
};

/**
 * The wording a person reads. The server's own messages are phrased
 * differently ("Subject is required for Email channel.") and are the
 * fallback a user sees only if this check is bypassed — so the two are
 * deliberately not unified, and each has exactly one home on its own side.
 */
export const CHANNEL_RULE_MESSAGES: Record<keyof ChannelRule, string> = {
  direction: "Direction is required.",
  subject: "Subject is required for emails.",
  outcome: "Outcome is required for calls.",
  modality: "Modality is required for meetings.",
};

/** Every field the full form shows for a channel — anything not hidden. */
export function fullFormShows(channel: CommunicationChannel, field: keyof ChannelRule): boolean {
  return CHANNEL_RULES[channel][field] !== "hidden";
}

/**
 * What the quick modal shows: whatever the channel requires, plus Outcome
 * wherever one is offered at all.
 */
export function quickFormShows(channel: CommunicationChannel, field: keyof ChannelRule): boolean {
  const rule = CHANNEL_RULES[channel][field];
  if (rule === "hidden") return false;
  return field === "outcome" ? true : rule === "required";
}

/** True when a channel insists on this field. Both forms share this. */
export function isRequired(channel: CommunicationChannel, field: keyof ChannelRule): boolean {
  return CHANNEL_RULES[channel][field] === "required";
}

/**
 * The channel-specific validation both forms run, given what the user has
 * typed. Returns a field→message map; empty means the channel rules pass.
 *
 * `shows` decides which questions this form asked — a form must never refuse
 * a value it never requested.
 */
export function validateChannelFields(
  channel: CommunicationChannel,
  values: { direction?: string | null; subject?: string | null; outcome?: string | null; modality?: string | null },
  shows: (channel: CommunicationChannel, field: keyof ChannelRule) => boolean,
): Partial<Record<keyof ChannelRule, string>> {
  const errors: Partial<Record<keyof ChannelRule, string>> = {};
  const fields: (keyof ChannelRule)[] = ["direction", "subject", "outcome", "modality"];

  for (const field of fields) {
    if (!shows(channel, field)) continue;
    if (!isRequired(channel, field)) continue;
    if (!values[field]?.trim()) errors[field] = CHANNEL_RULE_MESSAGES[field];
  }

  return errors;
}
